import { useGetReleaseById, useListReleases } from '@sunstone/api-client/hooks';
import {
  Button,
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@sunstone/ui';
import { Check, ChevronsUpDown, Loader2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useDebounce } from '@/hooks/useDebounce';

const PAGE_SIZE = 10;
const RELEASE_GROUP = 'baic';
// cmdk (via CommandItem's internal value tracking) treats an empty-string
// item value as "nothing selected" - such an item is clickable but can never
// be keyboard-highlighted or Enter-selected (aria-selected never becomes
// true). Use a non-empty sentinel for the "clear" item instead, same
// convention as the ALL='__all__' sentinel used by the plain <Select>
// filters elsewhere (e.g. WorkspacesPage's Status filter).
const CLEAR_VALUE = '__any_release__';

interface ReleaseComboboxProps {
  token?: string | null;
  value: string;
  onChange: (releaseId: string) => void;
  deployableOnly: boolean;
  releaseGroup?: string;
  placeholder?: string;
  className?: string;
  allowClear?: boolean;
  autoSelectFirst?: boolean;
}

/**
 * Searchable release picker/filter, backed by ListReleases. Shows only 10
 * releases by default and while searching - search is server-side, matched
 * against version/branch/repository (see ListReleases's SQL), not a
 * client-side filter over a large fetched page.
 */
export function ReleaseCombobox({
  token,
  value,
  onChange,
  deployableOnly,
  releaseGroup = RELEASE_GROUP,
  placeholder = 'Select a release…',
  className,
  allowClear = false,
  autoSelectFirst = false,
}: ReleaseComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);

  const { data, isLoading, isFetching } = useListReleases(token, releaseGroup, {
    pageSize: PAGE_SIZE,
    deployableOnly,
    includeDeprecated: !deployableOnly,
    search: debouncedSearch,
  });
  const releases = data?.releases ?? [];
  const totalCount = data?.totalCount ?? 0;
  const loading = isLoading || isFetching;

  const selectedFromList = releases.find((r) => r.releaseId === value);
  const { data: selectedReleaseData } = useGetReleaseById(
    token,
    !selectedFromList && value ? value : undefined,
  );
  const selectedVersion = selectedFromList?.version ?? selectedReleaseData?.release?.version;

  useEffect(() => {
    if (autoSelectFirst && !value && !search && releases[0]) {
      onChange(releases[0].releaseId);
    }
  }, [autoSelectFirst, value, search, releases, onChange]);

  const showClearButton = allowClear && !!value;

  return (
    <div className="relative">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              'justify-between font-normal',
              className ?? 'w-full',
              showClearButton && 'pr-8',
            )}
          >
            <span className={cn('truncate', !selectedVersion && 'text-muted-foreground')}>
              {selectedVersion || placeholder}
            </span>
            {!showClearButton && <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search version, branch, repo…"
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              {loading ? (
                <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading releases…
                </div>
              ) : (
                <>
                  {releases.length === 0 && (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                      No matching release.
                    </div>
                  )}
                  <CommandGroup>
                    {allowClear && (
                      <CommandItem
                        value={CLEAR_VALUE}
                        onSelect={() => {
                          onChange('');
                          setOpen(false);
                        }}
                      >
                        <Check className={value === '' ? 'opacity-100' : 'opacity-0'} />
                        Any release
                      </CommandItem>
                    )}
                    {releases.map((r) => (
                      <CommandItem
                        key={r.releaseId}
                        value={r.releaseId}
                        onSelect={() => {
                          onChange(r.releaseId);
                          setOpen(false);
                        }}
                      >
                        <Check className={r.releaseId === value ? 'opacity-100' : 'opacity-0'} />
                        <span className="flex-1">{r.version}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}
            </CommandList>
            {!loading && totalCount > releases.length && (
              <div className="border-t px-3 py-2 text-xs text-muted-foreground">
                Showing {releases.length} of {totalCount} — refine your search.
              </div>
            )}
          </Command>
        </PopoverContent>
      </Popover>
      {showClearButton && (
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          className="absolute right-2 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground hover:bg-transparent hover:text-foreground"
          onClick={(e) => {
            e.stopPropagation();
            onChange('');
          }}
        >
          <X className="size-3" />
          <span className="sr-only">Clear release</span>
        </Button>
      )}
    </div>
  );
}
